import {
  updatePluginToolbar,
  updateBoxAnimation,
  deleteBoxAnimation,
  updateBoxExitAnimation,
  deleteBoxExitAnimation,
} from "../../common/actions";
import {
  TEXT_FORMAT_CSS_PROP,
  forceHtmlCssProperty,
  stripHtmlCssProperty,
} from "../../common/common_tools";
import { isTextPlugin } from "./styleClipboard";

// Inline styles in `__text` win over box state, so rewrite the HTML too (same as
// EditorApp.applyMultiTextValue). font-size is em-based and would compound with
// the wrapper's em, so it is stripped; `null` (no explicit value) is stripped too.
function restyleHtml(html, textFormat) {
  let out = html;
  Object.keys(textFormat).forEach((key) => {
    const mapping = TEXT_FORMAT_CSS_PROP[key];
    if (!mapping) return;
    const [cssProp, toCssValue] = mapping;
    if (key === "textFontSize" || textFormat[key] === null) {
      out = stripHtmlCssProperty(out, cssProp);
      return;
    }
    out = forceHtmlCssProperty(out, cssProp, toCssValue(textFormat[key]));
  });
  return out;
}

function entries(obj) {
  const names = Object.keys(obj || {});
  return [names, names.map((name) => obj[name])];
}

export function applyStyleToBoxes(payload, ids, present, dispatch) {
  const result = { applied: 0, skipped: 0 };
  if (!payload || !Array.isArray(ids) || !ids.length || !dispatch) return result;
  const { boxesById = {}, pluginToolbarsById = {} } = present || {};

  // Same group → one undo entry (reducers.jsx groupBy).
  const previousGroup = window.cropUndoGroup;
  window.cropUndoGroup = "style-paste-" + Date.now();
  try {
    ids.forEach((id) => {
      const toolbar = pluginToolbarsById[id];
      const box = boxesById[id];
      if (!toolbar || !box || id === payload.sourceId) return;
      if (isTextPlugin(toolbar.pluginId) !== payload.isText) {
        result.skipped++;
        return;
      }
      result.applied++;

      const [styleNames, styleValues] = entries(payload.style);
      if (styleNames.length) {
        dispatch(updatePluginToolbar(id, "main", "style", styleNames, styleValues));
      }

      // Image adjustments only between same-plugin elements.
      const structurePatch = {
        ...(payload.transform || {}),
        ...(toolbar.pluginId === payload.pluginId ? payload.imageAdjust || {} : {}),
      };
      const [structureNames, structureValues] = entries(structurePatch);
      if (structureNames.length) {
        dispatch(updatePluginToolbar(id, "main", "structure", structureNames, structureValues));
      }

      if (payload.textFormat) {
        const [textNames, textValues] = entries(payload.textFormat);
        const html = toolbar.state && toolbar.state.__text;
        if (html) {
          textNames.push("__text");
          textValues.push(restyleHtml(html, payload.textFormat));
        }
        dispatch(updatePluginToolbar(id, "main", "state", textNames, textValues));
      }

      // No animation on the source clears the target's.
      if (payload.entryAnimation && payload.entryAnimation.animation) {
        const { animation, duration, delay } = payload.entryAnimation;
        dispatch(updateBoxAnimation(id, animation, duration, delay || 0));
      } else if (box.boxAnimation || box.textAnimation) {
        dispatch(deleteBoxAnimation(id));
      }

      if (payload.exitAnimation && payload.exitAnimation.animation) {
        const { animation, duration } = payload.exitAnimation;
        dispatch(updateBoxExitAnimation(id, animation, duration));
      } else if (box.boxExitAnimation) {
        dispatch(deleteBoxExitAnimation(id));
      }
    });
  } finally {
    window.cropUndoGroup = previousGroup;
  }

  return result;
}

export default applyStyleToBoxes;
